import React, { useState, useEffect, useMemo, Suspense, lazy } from 'react'
import { useSearchParams } from 'react-router-dom'
import './IntegrationTests.css'

const CompareView = lazy(() => import('./compare/CompareView'))
const MAX_COMPARE_SELECTION = 2

const INSIGHTS_ENDPOINTS = {
  'bot.dev.stratsys.net': 'https://data-ai-insights.svc.dev.stratsys.net',
  'bot.svc.dev.stratsys.net': 'https://data-ai-insights.svc.dev.stratsys.net',
  'bot.test.stratsys.net': 'https://data-ai-insights.svc.test.stratsys.net',
  'bot.stratsys.com': 'https://data-ai-insights.svc.stratsys.com',
  'localhost:8080': 'http://localhost:5001',
  'localhost:3000': 'http://localhost:5001'
}

function IntegrationTests() {
  const [date, setDate] = useState('')
  const [feature, setFeature] = useState('')
  const [batchName, setBatchName] = useState('')
  const [testLlmModel, setTestLlmModel] = useState('')
  const [appLlmModel, setAppLlmModel] = useState('')
  const [filterOptions, setFilterOptions] = useState({ batch_names: [], test_llm_models: [], app_llm_models: [], features: [] })
  const [runs, setRuns] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedRun, setSelectedRun] = useState(null)
  const [runTests, setRunTests] = useState([])
  const [loadingTests, setLoadingTests] = useState(false)
  const [selectedTest, setSelectedTest] = useState(null)
  const [stats, setStats] = useState([])
  const [selectedStatTest, setSelectedStatTest] = useState(null)
  const [statDetail, setStatDetail] = useState(null)
  const [loadingStatDetail, setLoadingStatDetail] = useState(false)
  const [selectedForCompare, setSelectedForCompare] = useState([])
  const [searchParams, setSearchParams] = useSearchParams()

  const baseUrl = INSIGHTS_ENDPOINTS[window.location.host] || ''

  const compareParam = searchParams.get('compare')
  const compareIds = useMemo(() => (compareParam ? compareParam.split(',').filter(Boolean) : []), [compareParam])
  const [compareRunIdA, compareRunIdB] = compareIds

  const toggleCompareSelection = (runId) => {
    setSelectedForCompare((prev) => {
      if (prev.includes(runId)) return prev.filter((id) => id !== runId)
      if (prev.length >= MAX_COMPARE_SELECTION) return prev
      return [...prev, runId]
    })
  }

  const openCompare = () => {
    if (selectedForCompare.length !== MAX_COMPARE_SELECTION) return
    setSearchParams({ compare: selectedForCompare.join(',') })
  }

  const closeCompare = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('compare')
    setSearchParams(next)
  }

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await window.PDR.api.get(`${baseUrl}/integration_tests/filter_options`)
        setFilterOptions(response)
      } catch (e) {
        console.error('Failed to fetch filter options', e)
      }
    }
    fetchFilterOptions()
  }, [baseUrl])

  const buildFilterParams = () => {
    const params = new URLSearchParams()
    if (feature) params.set('feature', feature)
    if (batchName) params.set('batch_name', batchName)
    if (testLlmModel) params.set('test_llm_model', testLlmModel)
    if (appLlmModel) params.set('app_llm_model', appLlmModel)
    return params
  }

  const fetchRuns = async () => {
    setLoading(true)
    setError(null)
    setSelectedRun(null)
    setSelectedTest(null)
    setSelectedStatTest(null)
    setStatDetail(null)
    try {
      const params = buildFilterParams()
      if (date) params.set('date', date)
      const qs = params.toString()
      const url = `${baseUrl}/integration_tests${qs ? `?${qs}` : ''}`
      const response = await window.PDR.api.get(url)
      setRuns(response)

      const statsParams = buildFilterParams()
      if (date) statsParams.set('date_from', date)
      if (date) statsParams.set('date_to', date)
      const statsQs = statsParams.toString()
      const statsUrl = `${baseUrl}/integration_tests/stats${statsQs ? `?${statsQs}` : ''}`
      const statsResponse = await window.PDR.api.get(statsUrl)
      setStats(statsResponse)
    } catch (e) {
      console.error(e)
      setError('Failed to fetch test runs')
    } finally {
      setLoading(false)
    }
  }

  const fetchRunTests = async (runId) => {
    setLoadingTests(true)
    setSelectedTest(null)
    try {
      const response = await window.PDR.api.get(`${baseUrl}/integration_tests?run_id=${runId}`)
      setRunTests(response)
      setSelectedRun(runId)
    } catch (e) {
      console.error(e)
      setError('Failed to fetch test details')
    } finally {
      setLoadingTests(false)
    }
  }

  const fetchStatDetail = async (testName) => {
    setLoadingStatDetail(true)
    setSelectedStatTest(testName)
    setStatDetail(null)
    try {
      const params = buildFilterParams()
      if (date) params.set('date_from', date)
      if (date) params.set('date_to', date)
      params.set('test_name', testName)
      const qs = params.toString()
      const response = await window.PDR.api.get(`${baseUrl}/integration_tests/stats/detail?${qs}`)
      setStatDetail(response)
    } catch (e) {
      console.error(e)
      setError('Failed to fetch test detail stats')
    } finally {
      setLoadingStatDetail(false)
    }
  }

  const formatTimestamp = (ts) => {
    if (!ts) return '—'
    const d = new Date(ts)
    return d.toLocaleString('sv-SE')
  }

  const statusBadge = (status) => {
    const cls = `status-badge status-${status}`
    const label = status === 'passed_with_warnings' ? 'passed ⚠️' : status
    return <span className={cls}>{label}</span>
  }

  const clearAllFilters = () => {
    setDate('')
    setFeature('')
    setBatchName('')
    setTestLlmModel('')
    setAppLlmModel('')
  }

  const hasFilters = date || feature || batchName || testLlmModel || appLlmModel

  return (
    <div className="integration-tests-container">
      <div className="one-ux-typography--heading-500 page-title">Integration Test Dashboard</div>

      {/* Filters */}
      <div className="filter-bar">
        <one-ux-field label="Date">
          <one-ux-dropdown placeholder="Select date">
            <one-ux-date-picker
              value={date ? [date] : []}
              onChange={(e) => {
                const val = e.target.value
                setDate(Array.isArray(val) ? val[0] || '' : val)
              }}
            />
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-field label="Feature">
          <one-ux-dropdown
            placeholder="All features"
            value={feature ? [feature] : []}
            onInput={(e) => {
              const val = e.target.value
              setFeature(Array.isArray(val) ? val[0] || '' : val)
            }}>
            <one-ux-list options={filterOptions.features.map((f) => ({ value: f, text: f }))} />
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-field label="Batch">
          <one-ux-dropdown
            placeholder="All batches"
            value={batchName ? [batchName] : []}
            onInput={(e) => {
              const val = e.target.value
              setBatchName(Array.isArray(val) ? val[0] || '' : val)
            }}>
            <one-ux-list options={filterOptions.batch_names.map((b) => ({ value: b, text: b }))} />
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-field label="Test LLM">
          <one-ux-dropdown
            placeholder="All models"
            value={testLlmModel ? [testLlmModel] : []}
            onInput={(e) => {
              const val = e.target.value
              setTestLlmModel(Array.isArray(val) ? val[0] || '' : val)
            }}>
            <one-ux-list options={filterOptions.test_llm_models.map((m) => ({ value: m, text: m }))} />
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-field label="App LLM">
          <one-ux-dropdown
            placeholder="All models"
            value={appLlmModel ? [appLlmModel] : []}
            onInput={(e) => {
              const val = e.target.value
              setAppLlmModel(Array.isArray(val) ? val[0] || '' : val)
            }}>
            <one-ux-list options={filterOptions.app_llm_models.map((m) => ({ value: m, text: m }))} />
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-button label="Fetch Runs" purpose="default" weight="normal" onClick={fetchRuns}>
          <one-ux-icon icon="search" slot="start" set="default"></one-ux-icon>
        </one-ux-button>

        {hasFilters && (
          <one-ux-button label="Clear All" purpose="notice" implicit onClick={clearAllFilters}></one-ux-button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading && <div className="loader"></div>}

      {/* Compare View */}
      {compareRunIdA && compareRunIdB && (
        <Suspense fallback={<div className="loader"></div>}>
          <CompareView baseUrl={baseUrl} runIdA={compareRunIdA} runIdB={compareRunIdB} runsFromParent={runs} onClose={closeCompare} />
        </Suspense>
      )}

      {/* Runs Table */}
      {!loading && runs.length > 0 && (
        <div className="runs-section">
          <div className="runs-section-header">
            <one-ux-text class="one-ux-typography--heading-400">Test Runs ({runs.length})</one-ux-text>
            <one-ux-button
              label={`Compare selected (${selectedForCompare.length}/${MAX_COMPARE_SELECTION})`}
              purpose="default"
              weight="normal"
              disabled={selectedForCompare.length !== MAX_COMPARE_SELECTION}
              onClick={openCompare}>
              <one-ux-icon icon="compare" slot="start" set="default"></one-ux-icon>
            </one-ux-button>
          </div>
          <table className="runs-table">
            <thead>
              <tr>
                <th className="run-select-cell">Compare</th>
                <th>Date / Time</th>
                <th>Feature</th>
                <th>Batch</th>
                <th>App LLM</th>
                <th>Environment</th>
                <th>Total</th>
                <th>Passed</th>
                <th>⚠️</th>
                <th>Failed</th>
                <th>Skipped</th>
                <th>Stmt %</th>
                <th>Link %</th>
                <th>Faithfulness</th>
                <th>Relevancy</th>
                <th>Ctx Relevancy</th>
                <th>Run ID</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr
                  key={run.test_run_id}
                  className={`run-row ${selectedRun === run.test_run_id ? 'selected' : ''}`}
                  onClick={() => fetchRunTests(run.test_run_id)}>
                  <td className="run-select-cell" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedForCompare.includes(run.test_run_id)}
                      disabled={!selectedForCompare.includes(run.test_run_id) && selectedForCompare.length >= MAX_COMPARE_SELECTION}
                      onChange={() => toggleCompareSelection(run.test_run_id)}
                    />
                  </td>
                  <td>{formatTimestamp(run.timestamp)}</td>
                  <td>{run.feature || '—'}</td>
                  <td>{run.batch_name || '—'}</td>
                  <td>{run.app_llm_model || '—'}</td>
                  <td>{run.environment || '—'}</td>
                  <td>{run.total}</td>
                  <td className="count-passed">{run.passed}</td>
                  <td className="count-passed_with_warnings">{run.passed_with_warnings || 0}</td>
                  <td className="count-failed">{run.failed}</td>
                  <td className="count-skipped">{run.skipped}</td>
                  <td className={run.statement_pass_rate === 100 ? 'count-passed' : run.statement_pass_rate != null && run.statement_pass_rate < 80 ? 'count-failed' : ''}>
                    {run.statement_pass_rate != null
                      ? `${run.statement_pass_rate}% (${run.statement_counts.passed}/${run.statement_counts.total})`
                      : '—'}
                  </td>
                  <td className={run.link_pass_rate === 100 ? 'count-passed' : run.link_pass_rate != null && run.link_pass_rate < 80 ? 'count-failed' : ''}>
                    {run.link_pass_rate != null
                      ? `${run.link_pass_rate}% (${run.link_counts.passed}/${run.link_counts.total})`
                      : '—'}
                  </td>
                  <td className={run.avg_rag_metrics?.faithfulness != null ? (run.avg_rag_metrics.faithfulness >= 0.8 ? 'count-passed' : run.avg_rag_metrics.faithfulness >= 0.5 ? 'count-passed_with_warnings' : 'count-failed') : ''}>
                    {run.avg_rag_metrics?.faithfulness != null ? run.avg_rag_metrics.faithfulness.toFixed(2) : '—'}
                  </td>
                  <td className={run.avg_rag_metrics?.answer_relevancy != null ? (run.avg_rag_metrics.answer_relevancy >= 0.8 ? 'count-passed' : run.avg_rag_metrics.answer_relevancy >= 0.5 ? 'count-passed_with_warnings' : 'count-failed') : ''}>
                    {run.avg_rag_metrics?.answer_relevancy != null ? run.avg_rag_metrics.answer_relevancy.toFixed(2) : '—'}
                  </td>
                  <td className={run.avg_rag_metrics?.contextual_relevancy != null ? (run.avg_rag_metrics.contextual_relevancy >= 0.8 ? 'count-passed' : run.avg_rag_metrics.contextual_relevancy >= 0.5 ? 'count-passed_with_warnings' : 'count-failed') : ''}>
                    {run.avg_rag_metrics?.contextual_relevancy != null ? run.avg_rag_metrics.contextual_relevancy.toFixed(2) : '—'}
                  </td>
                  <td className="run-id">{run.test_run_id.slice(0, 8)}…</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Per-Test Statistics */}
      {!loading && stats.length > 0 && (
        <div className="stats-section">
          <one-ux-text class="one-ux-typography--heading-400">Test Statistics</one-ux-text>
          <table className="runs-table">
            <thead>
              <tr>
                <th>Test Name</th>
                <th>Total Runs</th>
                <th>Passed</th>
                <th>⚠️</th>
                <th>Failed</th>
                <th>Pass Rate</th>
                <th>Fastest (s)</th>
                <th>Avg (s)</th>
                <th>Slowest (s)</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => (
                <tr key={s.test_name} className="run-row" onClick={() => fetchStatDetail(s.test_name)}>
                  <td>{s.test_name}</td>
                  <td>{s.total}</td>
                  <td className="count-passed">{s.passed}</td>
                  <td className="count-passed_with_warnings">{s.passed_with_warnings || 0}</td>
                  <td className="count-failed">{s.failed}</td>
                  <td className={s.pass_rate === 100 ? 'count-passed' : s.pass_rate === 0 ? 'count-failed' : ''}>
                    {s.pass_rate}%
                  </td>
                  <td>{s.fastest != null ? s.fastest : '—'}</td>
                  <td>{s.avg != null ? s.avg : '—'}</td>
                  <td>{s.slowest != null ? s.slowest : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && runs.length === 0 && !error && (
        <one-ux-text class="one-ux-typography--body-400 empty-state">
          No test runs found. Select a date and click Fetch Runs.
        </one-ux-text>
      )}

      {/* Run Detail Popup */}
      {selectedRun && (
        <div className="popup-overlay" onClick={() => setSelectedRun(null)}>
          <div className="popup-content run-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <div className="one-ux-typography--heading-400">Run: {selectedRun.slice(0, 8)}…</div>
              <one-ux-button label="Close" purpose="default" implicit onClick={() => setSelectedRun(null)}>
                <one-ux-icon icon="close" set="default"></one-ux-icon>
              </one-ux-button>
            </div>

            {loadingTests && <div className="loader"></div>}

            {!loadingTests && (
              <one-ux-scroll>
                <table className="tests-table">
                  <thead>
                    <tr>
                      <th>Test Name</th>
                      <th>File</th>
                      <th>Status</th>
                      <th>Duration (s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runTests.map((test, idx) => (
                      <tr
                        key={idx}
                        className={`test-row ${selectedTest === idx ? 'selected' : ''}`}
                        onClick={() => setSelectedTest(idx)}>
                        <td>{test.test_name}</td>
                        <td>{test.test_file}</td>
                        <td>{statusBadge(test.status)}</td>
                        <td>{test.duration ? test.duration.toFixed(2) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </one-ux-scroll>
            )}
          </div>
        </div>
      )}

      {/* Test Detail Popup */}
      {selectedTest !== null && runTests[selectedTest] && (
        <TestDetailPopup test={runTests[selectedTest]} onClose={() => setSelectedTest(null)} />
      )}

      {/* Stat Detail Popup */}
      {selectedStatTest && (
        <TestStatDetailPopup
          testName={selectedStatTest}
          detail={statDetail}
          loading={loadingStatDetail}
          onClose={() => { setSelectedStatTest(null); setStatDetail(null) }}
        />
      )}
    </div>
  )
}

export function TestDetailContent({ test }) {
  const hasStatements = test.statements && test.statements.results
  const hasRagMetrics = test.rag_metrics
  const hasLinks = test.links
  const hasInput = test.input
  const hasOutput = test.output

  return (
    <one-ux-scroll>
      <div className="test-detail-body">
            {/* Overview */}
            <div className="detail-section">
              <div className="one-ux-typography--heading-300">Overview</div>
              <div className="detail-grid">
                <span className="detail-label">Status:</span>
                <span className={`status-badge status-${test.status}`}>{test.status}</span>
                <span className="detail-label">File:</span>
                <span>{test.test_file}</span>
                <span className="detail-label">Duration:</span>
                <span>{test.duration ? `${test.duration.toFixed(2)}s` : '—'}</span>
                <span className="detail-label">Timestamp:</span>
                <span>{new Date(test.timestamp).toLocaleString('sv-SE')}</span>
              </div>
            </div>

            {/* Input */}
            {hasInput && (
              <div className="detail-section">
                <div className="one-ux-typography--heading-300">Input</div>
                <div className="detail-code">{test.input.query}</div>
                {test.input.state && (
                  <details className="detail-expandable">
                    <summary>State</summary>
                    <pre className="detail-pre">{JSON.stringify(test.input.state, null, 2)}</pre>
                  </details>
                )}
              </div>
            )}

            {/* Output */}
            {hasOutput && (
              <div className="detail-section">
                <div className="one-ux-typography--heading-300">Output</div>
                <div className="detail-code output-text">{test.output}</div>
              </div>
            )}

            {/* Error */}
            {test.error_message && (
              <div className="detail-section">
                <div className="one-ux-typography--heading-300">Error</div>
                <pre className="detail-pre error-pre">{test.error_message}</pre>
              </div>
            )}

            {/* Optional Failures */}
            {test.optional_failures && test.optional_failures.length > 0 && (
              <div className="detail-section" style={{ borderColor: '#facc15' }}>
                <div className="one-ux-typography--heading-300">⚠️ Optional Checks Not Met</div>
                <ul style={{ margin: '4px 0', paddingLeft: '20px', fontSize: '14px' }}>
                  {test.optional_failures.map((msg, i) => (
                    <li key={i} style={{ color: '#a16207', marginBottom: '4px' }}>
                      {msg}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Statement Verdicts */}
            {hasStatements && (
              <div className="detail-section">
                <div className="one-ux-typography--heading-300">Statement Verdicts</div>
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>Statement</th>
                      <th>Grade</th>
                      <th>Result</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(test.statements.results).map(([stmt, result]) => (
                      <tr key={stmt}>
                        <td>{stmt}</td>
                        <td>
                          {result.grade === 'optional' ? (
                            <span style={{ color: '#a16207', fontWeight: 600 }}>optional</span>
                          ) : (
                            <span style={{ color: '#475569' }}>required</span>
                          )}
                        </td>
                        <td>{result.passed ? '✅ Pass' : result.grade === 'optional' ? '⚠️ Fail' : '❌ Fail'}</td>
                        <td>{result.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* RAG Metrics */}
            {hasRagMetrics && (
              <div className="detail-section">
                <div className="one-ux-typography--heading-300">RAG Metrics</div>
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Score</th>
                      <th>Result</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(test.rag_metrics).map(([name, m]) => (
                      <tr key={name}>
                        <td>{name}</td>
                        <td>{typeof m.score === 'number' ? m.score.toFixed(2) : '—'}</td>
                        <td>{m.passed ? '✅ Pass' : '❌ Fail'}</td>
                        <td>{m.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Summarization Metrics */}
            {test.summarization_metrics && (
              <div className="detail-section">
                <div className="one-ux-typography--heading-300">Summarization Metrics</div>
                <div className="detail-grid">
                  <span className="detail-label">Score:</span>
                  <span>
                    {typeof test.summarization_metrics.score === 'number'
                      ? test.summarization_metrics.score.toFixed(2)
                      : '—'}
                  </span>
                  <span className="detail-label">Result:</span>
                  <span>{test.summarization_metrics.passed ? '✅ Pass' : '❌ Fail'}</span>
                  <span className="detail-label">Threshold:</span>
                  <span>{test.summarization_metrics.threshold}</span>
                  <span className="detail-label">Reason:</span>
                  <span>{test.summarization_metrics.reason}</span>
                </div>
                {test.summarization_metrics.score_breakdown && (
                  <details className="detail-expandable">
                    <summary>Score Breakdown</summary>
                    <pre className="detail-pre">
                      {JSON.stringify(test.summarization_metrics.score_breakdown, null, 2)}
                    </pre>
                  </details>
                )}
                {test.summarization_metrics.assessment_questions && (
                  <details className="detail-expandable">
                    <summary>Assessment Questions</summary>
                    <pre className="detail-pre">
                      {JSON.stringify(test.summarization_metrics.assessment_questions, null, 2)}
                    </pre>
                  </details>
                )}
                {test.summarization_metrics.claims && test.summarization_metrics.claims.length > 0 && (
                  <details className="detail-expandable">
                    <summary>Claims ({test.summarization_metrics.claims.length})</summary>
                    <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {test.summarization_metrics.claims.map((claim, i) => (
                        <li key={i} style={{ marginBottom: '4px' }}>
                          {claim}
                        </li>
                      ))}
                    </ol>
                  </details>
                )}
                {test.summarization_metrics.alignment_verdicts &&
                  test.summarization_metrics.alignment_verdicts.length > 0 && (
                    <details className="detail-expandable">
                      <summary>Alignment Verdicts ({test.summarization_metrics.alignment_verdicts.length})</summary>
                      <table className="detail-table">
                        <thead>
                          <tr>
                            <th>Claim</th>
                            <th>Verdict</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {test.summarization_metrics.alignment_verdicts.map((v, i) => (
                            <tr key={i}>
                              <td>{test.summarization_metrics.claims?.[i] ?? `Claim ${i + 1}`}</td>
                              <td>{v.verdict === 'yes' ? '✅ yes' : v.verdict === 'no' ? '❌ no' : '❓ idk'}</td>
                              <td>{v.reason || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </details>
                  )}
              </div>
            )}

            {/* Links */}
            {hasLinks && (
              <div className="detail-section">
                <div className="one-ux-typography--heading-300">Link Verification</div>
                <div className="detail-grid">
                  <span className="detail-label">Result:</span>
                  <span>{test.links.passed ? '✅ Pass' : '❌ Fail'}</span>
                  <span className="detail-label">Expected:</span>
                  <span>{(test.links.expected || []).join(', ')}</span>
                  <span className="detail-label">Found:</span>
                  <span>{(test.links.found || []).join(', ') || 'None'}</span>
                </div>
              </div>
            )}

            {/* Sources */}
            {test.sources && test.sources.length > 0 && (
              <div className="detail-section">
                <div className="one-ux-typography--heading-300">Sources</div>
                <details className="detail-expandable">
                  <summary>{test.sources.length} source(s)</summary>
                  <pre className="detail-pre">{JSON.stringify(test.sources, null, 2)}</pre>
                </details>
              </div>
            )}

            {/* Retrieval Context */}
            {test.retrieval_context && test.retrieval_context.length > 0 && (
              <div className="detail-section">
                <div className="one-ux-typography--heading-300">Retrieval Context</div>
                <details className="detail-expandable">
                  <summary>{test.retrieval_context.length} context(s)</summary>
                  <pre className="detail-pre">{JSON.stringify(test.retrieval_context, null, 2)}</pre>
                </details>
              </div>
            )}
      </div>
    </one-ux-scroll>
  )
}

function TestDetailPopup({ test, onClose }) {
  return (
    <div className="popup-overlay test-detail-overlay" onClick={onClose}>
      <div className="popup-content test-detail-popup" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header">
          <div className="one-ux-typography--heading-400">{test.test_name}</div>
          <one-ux-button label="Close" purpose="default" implicit onClick={onClose}>
            <one-ux-icon icon="close" set="default"></one-ux-icon>
          </one-ux-button>
        </div>
        <TestDetailContent test={test} />
      </div>
    </div>
  )
}

function TestStatDetailPopup({ testName, detail, loading, onClose }) {
  return (
    <div className="popup-overlay test-detail-overlay" onClick={onClose}>
      <div className="popup-content test-detail-popup" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header">
          <div className="one-ux-typography--heading-400">{testName}</div>
          <one-ux-button label="Close" purpose="default" implicit onClick={onClose}>
            <one-ux-icon icon="close" set="default"></one-ux-icon>
          </one-ux-button>
        </div>

        {loading && <div className="loader"></div>}

        {!loading && detail && (
          <one-ux-scroll>
            <div className="test-detail-body">
              {/* Overview */}
              <div className="detail-section">
                <div className="one-ux-typography--heading-300">Overview</div>
                <div className="detail-grid">
                  <span className="detail-label">Runs:</span>
                  <span>{detail.run_count}</span>
                </div>
              </div>

              {/* RAG Metrics */}
              {detail.rag_metrics && Object.keys(detail.rag_metrics).length > 0 && (
                <div className="detail-section">
                  <div className="one-ux-typography--heading-300">RAG Metrics (across {detail.run_count} runs)</div>
                  <table className="detail-table">
                    <thead>
                      <tr>
                        <th>Metric</th>
                        <th>Lowest</th>
                        <th>Average</th>
                        <th>Highest</th>
                        <th>Samples</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(detail.rag_metrics).map(([name, m]) => (
                        <tr key={name}>
                          <td>{name}</td>
                          <td className={m.lowest < 0.5 ? 'count-failed' : ''}>{m.lowest.toFixed(3)}</td>
                          <td>{m.avg.toFixed(3)}</td>
                          <td className="count-passed">{m.highest.toFixed(3)}</td>
                          <td>{m.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Statement Pass Rates */}
              {detail.statements && detail.statements.length > 0 && (
                <div className="detail-section">
                  <div className="one-ux-typography--heading-300">Statement Pass Rates</div>
                  <table className="detail-table">
                    <thead>
                      <tr>
                        <th>Statement</th>
                        <th>Grade</th>
                        <th>Passed</th>
                        <th>Total</th>
                        <th>Pass Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.statements.map((s, i) => (
                        <tr key={i}>
                          <td>{s.statement}</td>
                          <td>{s.grade === 'optional'
                            ? <span style={{ color: '#a16207', fontWeight: 600 }}>optional</span>
                            : <span style={{ color: '#475569' }}>required</span>}
                          </td>
                          <td className="count-passed">{s.passed}</td>
                          <td>{s.total}</td>
                          <td className={s.pass_rate === 100 ? 'count-passed' : s.pass_rate < 50 ? 'count-failed' : ''}>
                            {s.pass_rate}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Link Pass Rates */}
              {detail.links && detail.links.length > 0 && (
                <div className="detail-section">
                  <div className="one-ux-typography--heading-300">Link Pass Rates</div>
                  <table className="detail-table">
                    <thead>
                      <tr>
                        <th>URL</th>
                        <th>Found</th>
                        <th>Total</th>
                        <th>Pass Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.links.map((l, i) => (
                        <tr key={i}>
                          <td style={{ wordBreak: 'break-all' }}>{l.url}</td>
                          <td className="count-passed">{l.found}</td>
                          <td>{l.total}</td>
                          <td className={l.pass_rate === 100 ? 'count-passed' : l.pass_rate < 50 ? 'count-failed' : ''}>
                            {l.pass_rate}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </one-ux-scroll>
        )}
      </div>
    </div>
  )
}

export default IntegrationTests
